import {announce} from '@react-aria/live-announcer'

import NotFoundSvg from '@/assets/icons/not-found.svg?react'
import searchSvg from '@/assets/icons/search.svg'
import LoadingIcon from '@/components/LoadingIcon'
import ProductCard from '@/components/ProductCard/ProductCard'
import ProductCardPlaceholder from '@/components/ProductCard/ProductCardPlaceholder'
import {SEARCH_EVENT} from '@/constants/events'
import useNumberOfItemsPerRow from '@/hooks/useNumberOfItemsPerRow'
import Pagination from '@/layout/Pagination'
import {sendMessage} from '@/lib/cobrowse/cobrowse'
import HeadTags from '@/lib/head/HeadTags'
import useSearchResult from '@/lib/home/hooks/useSearchResult'
import {useLocale} from '@/lib/locale/locale'
import {getProductQueryKey} from '@/lib/product/hooks/useProduct'
import preloadProduct from '@/lib/product/utils/preloadProduct'
import {HomeRoute} from '@/routeRegistry'
import skipTargetProps from '@/utils/a11y/skipTargetProps'
import VisuallyHidden from '@/utils/a11y/VisuallyHidden'
import useIdleTimeScheduler from '@/utils/hooks/useIdleTimeScheduler'
import useLogPageViewEvent from '@/utils/hooks/useLogPageViewEvent'
import useWindowSize from '@/utils/hooks/useWindowSize'
import useSearch from '@/utils/router/useSearch'
import ProductSorter, {SortBy} from '@/views/Home/components/ProductSorter/ProductSorter'

import style from './HomePageSearchResult.module.scss'

// Note: this component is virtualized, but it's not used in the current implementation because we have to hide amazon products
// const _ProductCards = memo<{
//   products: Product[]
//   ask: (q: string) => void
// }>(function ProductCards({products, ask}) {
//   const listRef = useRef<HTMLDivElement | null>(null)

//   const lanes = useNumberOfItemsPerRow()

//   const chunkedProducts = useMemo(() => Array.from(chunkify(products, lanes)), [products, lanes])

//   // console.log(chunkedProducts[0])

//   const {savedOffset: restoreOffset, savedMeasureCache: restoreMeasurementsCache} =
//     globalThis.history.state ?? {}

//   const virtualizer = useWindowVirtualizer({
//     count: chunkedProducts.length,
//     estimateSize: () => 600,
//     overscan: 2,
//     initialOffset: restoreOffset as number,
//     initialMeasurementsCache: restoreMeasurementsCache as VirtualItem[],
//     onChange: virtualizer => {
//       if (!virtualizer.isScrolling) {
//         globalThis.history.replaceState(
//           {
//             savedOffset: virtualizer.scrollOffset ?? 0,
//             savedMeasureCache: virtualizer.measurementsCache,
//           },
//           document.title,
//           globalThis.location.href,
//         )
//       }
//     },
//   })

//   const items = virtualizer.getVirtualItems()

//   return (
//     <div ref={listRef} className={style.Products} {...skipTargetProps('products')}>
//       <div
//         style={{
//           height: `${virtualizer.getTotalSize()}px`,
//           width: '100%',
//           position: 'relative',
//         }}
//       >
//         <div
//           style={{
//             position: 'absolute',
//             top: 0,
//             left: 0,
//             width: '100%',
//             transform: `translateY(${items[0]?.start ?? 0 - virtualizer.options.scrollMargin}px)`,
//           }}
//         >
//           {items.map(item => {
//             const chunkedProduct = chunkedProducts[item.index]
//             invariant(chunkedProduct)

//             return (
//               <div key={item.key} data-index={item.index} ref={virtualizer.measureElement}>
//                 {chunkedProduct.map(product => (
//                   <div className={style.Product} key={`${product.retailer}-${product.id}`}>
//                     <ProductCard
//                       product={product}
//                       isInMessage
//                       submitMessage={ask}
//                       deals={false}
//                       headingLevel={2}
//                     />
//                   </div>
//                 ))}
//               </div>
//             )
//           })}
//         </div>
//       </div>
//     </div>
//   )
// })

interface Props {
  q: string
}

const ITEMS_PER_PAGE = 20
const ITEMS_PER_PAGE_MOBILE = 10

export default memo<Props>(function HomePageSearchResult({q}) {
  const router = useRouter()
  const queryClient = useQueryClient()
  const latestQ = useLatest(q)
  // const askRef = useRef<AskInnoSearchControl>(null)
  const scheduler = useIdleTimeScheduler()
  const [locale] = useLocale()
  const [filter, setFilter] = useState('')
  const deferredQ = useDeferredValue(filter)
  const {isMobile} = useWindowSize()

  const {data: searchResult, isFetching} = useSearchResult({q})

  const [sorter, setSorter] = useState<SortBy>(SortBy.Relevance)

  const isFetched = !isFetching

  const filteredSearchResultProducts = useMemo(() => {
    if (!searchResult) return []

    return deferredQ
      ? searchResult.products.filter(product => {
          const q = deferredQ.toLowerCase()

          return Object.entries(product).some(([, value]: [string, unknown]) =>
            String(value).toLowerCase().includes(q),
          )
        })
      : searchResult.products
  }, [searchResult, deferredQ])

  const filteredAndSortedSearchResultProducts = useMemo(() => {
    return filteredSearchResultProducts.slice().sort((a, b) => {
      if (sorter === SortBy.Relevance) {
        return 0
      }

      try {
        const aPrice = parseFloat(a.price.toString())
        const bPrice = parseFloat(b.price.toString())

        if (sorter === SortBy.PriceAsc) {
          return aPrice - bPrice
        }

        return bPrice - aPrice
      } catch {
        return 0
      }
    })
  }, [filteredSearchResultProducts, sorter])

  const [page, setPage] = useSearch(HomeRoute.id, 'page', {
    replace: false,
    resetScroll: true,
    hashScrollIntoView: {
      behavior: 'smooth',
      block: 'start',
      inline: 'nearest',
    },
  })

  const itemsPerPage: number = isMobile ? ITEMS_PER_PAGE_MOBILE : ITEMS_PER_PAGE
  const pageCount = Math.ceil(filteredAndSortedSearchResultProducts.length / itemsPerPage) || 1 // Page count should never be zero
  const currentItems = filteredAndSortedSearchResultProducts.slice(
    (page - 1) * itemsPerPage,
    page * itemsPerPage,
  )

  ;(function correctPage() {
    if (isFetching) return
    if (page > pageCount) void setPage(pageCount)
  })()

  const handlePageChange = useCallback((event: {selected: number}) => {
    void setPage(event.selected + 1)
  }, [])

  const hrefBuilder = useCallback(
    (page: number) => {
      return router.buildLocation({
        to: HomeRoute.fullPath,
        search: {
          q: latestQ.current,
          page,
        },
      }).href
    },
    [router],
  )

  useEffect(
    function syncCache() {
      if (!searchResult) return

      searchResult.products.forEach(product => {
        queryClient.setQueryData(
          getProductQueryKey({retailer: product.retailer, id: product.id, locale}),
          product,
        )
      })
    },
    [searchResult, queryClient, locale],
  )

  // const ask = useCallback((q: string) => {
  //   askRef.current?.ask(q)
  // }, [])

  useEffect(() => {
    announce(`Searching for "${q}"`)
  }, [q])

  useEffect(
    function announceSearchResult() {
      if (!isFetched) return

      if (!searchResult || searchResult.products.length === 0) {
        announce(`No results for "${q}", try checking your spelling or use more general terms`)
        return
      }

      announce(`Found ${searchResult.products.length} results for "${q}"`)
    },
    [q, searchResult, isFetched],
  )

  useEffect(
    function preloadProducts() {
      if (filteredSearchResultProducts.length === 0) return

      filteredSearchResultProducts.slice(0, 10).forEach(product => {
        scheduler.schedule(() => {
          void preloadProduct(product, locale, queryClient)
        })
      })
    },
    [filteredSearchResultProducts, queryClient, locale],
  )

  if (searchResult === undefined) {
    return <HomePageSearchResultPlaceholder q={q} />
  }

  if (searchResult === false) {
    return (
      <div className={style.Container} role='alert'>
        <NotFoundSvg className={style.NotFoundSvg} aria-hidden />
        <div className={style.HeadingWrapper}>
          <h1 className={style.Heading}>No results for &quot;{q}&quot;</h1>
        </div>
        <span className={style.Description}>
          Try checking your spelling or use more general terms
        </span>
      </div>
    )
  }

  return (
    <>
      <HeadTags title={`Results for "${q}"`} />
      <div className={style.Container}>
        <div className={style.HeadingWrapper}>
          <h1 className={style.Heading}>
            {filteredSearchResultProducts.length.toString()} Results for &quot;{q}&quot;
            {!isFetched && (
              <LoadingIcon
                className={style.LoadingIcon}
                aria-label="Loading, please wait a moment (You may experience glitch if you move around while it's still loading)"
              />
            )}
            {pageCount > 1 && <VisuallyHidden>(page {page})</VisuallyHidden>}
          </h1>
          <div className={style.Actions}>
            <form
              role='search'
              className={style.SearchWrapper}
              onSubmit={e => {
                e.preventDefault()
                announce(
                  `${filteredSearchResultProducts.length.toString()} results filtered for "${
                    deferredQ
                  }"`,
                )
              }}
            >
              {/* eslint-disable-next-line jsx-a11y/role-supports-aria-props -- still works for some screen readers */}
              <input
                type='search'
                className={style.Search}
                value={filter}
                onChange={e => {
                  setFilter(e.currentTarget.value)
                }}
                placeholder='Filter results...'
                aria-description='Press enter to perform the filtering'
              />
              <button type='submit' className={style.SearchBtn} aria-label='Filter Search Results'>
                <img src={searchSvg} alt='' />
              </button>
            </form>
            <ProductSorter sorter={sorter} setSorter={setSorter} />
          </div>
        </div>
        {/* <ProductCards products={filteredAndSortedSearchResultProducts} ask={ask} /> */}
        <div className={style.Products} {...skipTargetProps('products')}>
          {currentItems.map(product => (
            <VisuallyHidden
              as='div'
              isVisible={product.retailer !== 'amazon' && product.seller !== 'amazon'}
              className={style.Product}
              key={`${product.retailer}-${product.id}`}
            >
              <ProductCard
                product={product}
                isInMessage
                submitMessage={sendMessage}
                deals={false}
                headingLevel={2}
              />
            </VisuallyHidden>
          ))}
        </div>
        {pageCount > 1 && (
          <Pagination
            onPageChange={handlePageChange}
            pageCount={pageCount}
            hrefBuilder={hrefBuilder}
            currentPage={page - 1}
          />
        )}
      </div>
      {/* {searchResult.thread_id && (
        <AskInnoSearch
          threadId={searchResult.thread_id}
          prompts={searchResult.suggestedPrompts}
          ref={askRef}
        />
      )} */}
    </>
  )
})

const HomePageSearchResultPlaceholder = memo<{
  q: string
}>(function HomePageSearchResultPlaceholder({q}) {
  useLogPageViewEvent(SEARCH_EVENT)

  const numberOfItemsPerRow = useNumberOfItemsPerRow()

  const dummy = new Array(numberOfItemsPerRow).fill(undefined)

  return (
    <div className={style.Container}>
      <div className={style.HeadingWrapper}>
        <h1 className={style.Heading}>
          Results for &quot;{q}&quot;
          <LoadingIcon className={style.LoadingIcon} />
        </h1>
      </div>
      <div className={style.Products}>
        {dummy.map((_, idx) => (
          <div className={style.Product} key={idx}>
            <ProductCardPlaceholder />
          </div>
        ))}
      </div>
    </div>
  )
})
